import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, ChevronDown, LockKeyhole, Mail } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import { useCart } from "../state/cart";
import { useToast } from "../state/toast";
import { CheckoutSteps } from "../components/brand";
import { Dialog } from "../components/Dialog";
import { Seo } from "../components/Seo";

const RESEND_COOLDOWN_SECONDS = 45;
// Deliberately simple/lenient (not RFC-perfect) — just enough to catch real typos client-side.
// Needed because some mobile in-app browsers (Facebook, TikTok, etc.) don't reliably enforce
// native type="email" validation, letting malformed addresses reach the server, which rejects
// them with a generic error that doesn't point back at this field.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Shipping = { publicId: string; name: string; type: "PICKUP" | "FIXED" | "LOCATION_RULE"; feeCentavos: number };
type Content = { settings: { checkout?: { emailVerificationRequired?: boolean } } };
type Form = {
  name: string; email: string; mobile: string; shippingMethodPublicId: string;
  line1: string; line2: string; city: string; province: string; postalCode: string;
  notes: string; termsAccepted: boolean;
};
type PendingVerification = { publicId: string; email: string };

export function CheckoutPage() {
  const cart = useCart(); const navigate = useNavigate(); const toast = useToast();
  const shipping = useQuery({ queryKey: ["shipping"], queryFn: () => api<Shipping[]>("/shipping-methods") });
  const content = useQuery({ queryKey: ["content"], queryFn: () => api<Content>("/content") });
  const emailVerificationRequired = content.data?.settings.checkout?.emailVerificationRequired ?? false;
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<Form>({ defaultValues: { termsAccepted: false } });
  const methodId = watch("shippingMethodPublicId");
  const method = shipping.data?.find((item) => item.publicId === methodId);
  useEffect(() => { if (!methodId && shipping.data?.[0]) setValue("shippingMethodPublicId", shipping.data[0].publicId); }, [shipping.data, methodId, setValue]);

  const [verified, setVerified] = useState<PendingVerification | null>(null);
  const [pendingCode, setPendingCode] = useState<PendingVerification | null>(null);
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const pendingFormData = useRef<Form | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => setCooldown((current) => Math.max(0, current - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const submit = useMutation({
    mutationFn: (data: Form & { emailVerificationPublicId?: string }) => {
      const requestBody = JSON.stringify({
        customer: { name: data.name, email: data.email, mobile: data.mobile },
        emailVerificationPublicId: data.emailVerificationPublicId,
        fulfillmentType: method?.type === "PICKUP" ? "PICKUP" : "SHIPPING",
        shippingMethodPublicId: data.shippingMethodPublicId,
        address: method?.type === "PICKUP" ? undefined : { line1: data.line1, line2: data.line2 || undefined, city: data.city, province: data.province, postalCode: data.postalCode },
        notes: data.notes || undefined, termsAccepted: data.termsAccepted,
        items: cart.items.map((item) => ({
          productPublicId: item.productPublicId, quantity: item.quantity, variantId: item.variant?.id,
          addonIds: item.addons.map((addon) => addon.id),
          customizations: item.customizations.map((custom) => ({ fieldId: custom.fieldId, value: custom.value })),
          referenceFilePublicIds: item.referenceFiles.map((file) => file.publicId)
        }))
      });
      return api<{ orderNumber: string; status: string; totals: { totalCentavos: number } }>("/orders", {
        method: "POST",
        headers: { "Idempotency-Key": checkoutIdempotencyKey(requestBody) },
        body: requestBody
      });
    },
    onSuccess: (result, data) => {
      sessionStorage.removeItem(CHECKOUT_REQUEST_KEY);
      sessionStorage.setItem(`order-${result.orderNumber}`, JSON.stringify({ contact: data.email, totalCentavos: result.totals.totalCentavos }));
      cart.clear(); navigate(`/payment/${result.orderNumber}`);
    }
  });

  const sendCode = useMutation({
    mutationFn: (email: string) => api<{ publicId: string; expiresInMinutes: number }>("/email-verifications", {
      method: "POST", body: JSON.stringify({ email })
    }),
    onSuccess: (result, email) => {
      setPendingCode({ publicId: result.publicId, email });
      setCode("");
      setCooldown(RESEND_COOLDOWN_SECONDS);
      toast.success(`Code sent to ${email}.`);
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Could not send the verification code.")
  });

  const confirmCode = useMutation({
    mutationFn: () => api<{ publicId: string; email: string }>(`/email-verifications/${pendingCode!.publicId}/confirm`, {
      method: "POST", body: JSON.stringify({ code })
    }),
    onSuccess: (result) => {
      setVerified({ publicId: result.publicId, email: result.email });
      setPendingCode(null);
      setCode("");
      const data = pendingFormData.current;
      pendingFormData.current = null;
      if (data) submit.mutate({ ...data, emailVerificationPublicId: result.publicId });
    }
  });

  const startCheckout = (data: Form) => {
    if (!emailVerificationRequired) {
      submit.mutate(data);
      return;
    }
    if (verified?.email === data.email) {
      submit.mutate({ ...data, emailVerificationPublicId: verified.publicId });
      return;
    }
    pendingFormData.current = data;
    sendCode.mutate(data.email);
  };

  if (!cart.items.length && !submit.isSuccess) return <section className="container-page py-24 text-center"><Seo title="Checkout" path="/checkout" noindex /><h1 className="font-display text-4xl text-cocoa">Your bag is empty</h1><p className="mt-3 text-muted">Add a piece to your bag before checking out.</p><Link className="btn-primary mt-8" to="/shop">Browse the shop</Link></section>;
  const shippingFee = method?.feeCentavos ?? 0;
  const total = cart.subtotalCentavos + shippingFee;
  const summary = <>
    <ul className="space-y-4">{cart.items.map((item) => <li key={item.id} className="flex gap-3">
      <span className="relative shrink-0"><img src={item.imageUrl} alt="" className="size-14 rounded-[4px] bg-white object-cover" /><span className="absolute -right-1.5 -top-1.5 grid size-5 place-items-center rounded-full bg-cocoa text-[11px] font-bold text-white">{item.quantity}</span></span>
      <div className="min-w-0 flex-1"><p className="truncate text-[15px] font-bold">{item.name}</p>{item.variant && <p className="text-[13px] text-muted">{item.variant.name}</p>}</div>
      <p className="text-[15px] font-bold tabular-nums">{formatMoney(item.unitPriceCentavos * item.quantity)}</p>
    </li>)}</ul>
    <dl className="mt-5 space-y-2.5 border-t border-cocoa/10 pt-5 text-[15px]">
      <div className="flex justify-between"><dt className="text-muted">Subtotal</dt><dd className="tabular-nums">{formatMoney(cart.subtotalCentavos)}</dd></div>
      <div className="flex justify-between"><dt className="text-muted">{method?.name ?? "Pickup or delivery"}</dt><dd className="tabular-nums">{shippingFee ? formatMoney(shippingFee) : "Free"}</dd></div>
      <div className="flex justify-between border-t border-cocoa/10 pt-3 text-lg font-bold"><dt>Total</dt><dd className="tabular-nums">{formatMoney(total)}</dd></div>
    </dl>
    <p className="mt-4 flex gap-2 text-[13px] leading-relaxed text-muted"><LockKeyhole size={15} className="mt-0.5 shrink-0" /> The final amount is recalculated and locked in when you continue.</p>
  </>;
  const closeVerification = () => { setPendingCode(null); setCode(""); pendingFormData.current = null; };
  return (
    <section className="container-page pb-10 pt-8 md:pt-12">
      <Seo title="Checkout" path="/checkout" noindex />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <CheckoutSteps current={1} />
        <Link to="/cart" className="flex items-center gap-1.5 text-sm font-bold text-muted hover:text-cocoa"><ArrowLeft size={16} /> Back to bag</Link>
      </div>
      <h1 className="mt-6 font-display text-[2.5rem] leading-none text-cocoa md:text-5xl">Checkout</h1>

      <details className="group mt-6 rounded-xl bg-frosting lg:hidden">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4 font-bold [&::-webkit-details-marker]:hidden">
          <span className="flex items-center gap-2">Order summary <ChevronDown size={17} className="transition-transform group-open:rotate-180" /></span>
          <span className="tabular-nums">{formatMoney(total)}</span>
        </summary>
        <div className="px-5 pb-5">{summary}</div>
      </details>

      <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-14">
        <form onSubmit={handleSubmit(startCheckout)} noValidate className="divide-y divide-line lg:col-span-7">
          <fieldset className="pb-8">
            <legend className="float-left mb-5 w-full text-lg font-bold">Contact</legend>
            <div className="clear-both grid gap-4 sm:grid-cols-2">
              <Field label="Full name" error={errors.name?.message}><input className="field" autoComplete="name" {...register("name", { required: "Tell us your name.", validate: (value) => value.trim().length >= 2 || "Use at least 2 characters." })} /></Field>
              <Field label="Email" error={errors.email?.message}><input type="email" inputMode="email" autoComplete="email" className="field" {...register("email", { required: "An email is required so we can send order updates.", pattern: { value: EMAIL_PATTERN, message: "Enter a valid email address." } })} /></Field>
              <div className="sm:col-span-2"><Field label="Mobile number" optional error={errors.mobile?.message}><input className="field" inputMode="tel" autoComplete="tel" {...register("mobile", { validate: (value) => !value || value.trim().length >= 7 || "Check this number." })} /></Field></div>
            </div>
            {emailVerificationRequired && <p className="mt-3 flex items-center gap-2 text-[13px] text-muted"><Mail size={14} className="shrink-0" /> We’ll email a one-time code to confirm this address before your order is created.</p>}
          </fieldset>

          <fieldset className="py-8">
            <legend className="float-left mb-5 w-full text-lg font-bold">Pickup or delivery</legend>
            <div className="clear-both grid gap-2">{shipping.data?.map((option) => <label key={option.publicId} data-selected={methodId === option.publicId} className="choice">
              <input type="radio" value={option.publicId} className="size-5 accent-cocoa" {...register("shippingMethodPublicId", { required: true })} />
              <span className="flex-1 font-bold">{option.name}</span>
              <span className="font-bold tabular-nums">{option.feeCentavos ? formatMoney(option.feeCentavos) : "Free"}</span>
            </label>)}</div>
          </fieldset>

          {method && method.type !== "PICKUP" && <fieldset className="py-8">
            <legend className="float-left mb-5 w-full text-lg font-bold">Delivery address</legend>
            <div className="clear-both grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><Field label="Street address" error={errors.line1?.message}><input className="field" autoComplete="address-line1" {...register("line1", { required: "Delivery address is required." })} /></Field></div>
              <div className="sm:col-span-2"><Field label="Unit or landmark" optional><input className="field" autoComplete="address-line2" {...register("line2")} /></Field></div>
              <Field label="City" error={errors.city?.message}><input className="field" autoComplete="address-level2" {...register("city", { required: "City is required." })} /></Field>
              <Field label="Province" error={errors.province?.message}><input className="field" autoComplete="address-level1" {...register("province", { required: "Province is required." })} /></Field>
              <Field label="Postal code" error={errors.postalCode?.message}><input className="field" inputMode="numeric" autoComplete="postal-code" {...register("postalCode", { required: "Postal code is required." })} /></Field>
            </div>
          </fieldset>}

          <div className="py-8">
            <Field label="Notes for Cass" optional><textarea className="field min-h-24" placeholder="Gift message, timing, anything we should know" {...register("notes")} /></Field>
          </div>

          <div className="pt-8">
            <label className="flex cursor-pointer items-start gap-3">
              <input type="checkbox" className="mt-1 size-5 shrink-0 accent-cocoa" {...register("termsAccepted", { required: "Please confirm the terms to continue." })} />
              <span className="text-[15px] leading-relaxed">My details are correct, and I understand making begins only after my payment is reviewed and approved. I agree to the <Link to="/terms-of-use" target="_blank" className="font-bold underline decoration-cocoa/30 underline-offset-4">Terms of Use</Link> and <Link to="/privacy-policy" target="_blank" className="font-bold underline decoration-cocoa/30 underline-offset-4">Privacy Policy</Link>.</span>
            </label>
            {errors.termsAccepted && <p role="alert" className="mt-2 text-sm font-bold text-ribbon-ink">{errors.termsAccepted.message}</p>}
            {submit.isError && <div role="alert" className="mt-5 rounded-[10px] bg-blush/50 px-4 py-3 font-bold text-ribbon-ink">{submit.error.message}</div>}
            <div className="mt-6 flex items-center justify-between gap-4 lg:hidden"><span className="text-muted">Total</span><span className="text-lg font-bold tabular-nums">{formatMoney(total)}</span></div>
            <button className="btn-primary mt-4 w-full lg:mt-6" disabled={submit.isPending || sendCode.isPending}>{submit.isPending ? "Creating your order…" : sendCode.isPending ? "Sending your code…" : <>Continue to payment <ArrowRight size={18} /></>}</button>
          </div>
        </form>

        <aside className="hidden h-fit rounded-xl bg-frosting p-6 lg:sticky lg:top-24 lg:col-span-5 lg:block">
          <h2 className="mb-5 text-lg font-bold">Order summary</h2>
          {summary}
        </aside>
      </div>

      <Dialog
        open={Boolean(pendingCode)}
        onClose={closeVerification}
        variant="center"
        title="Check your email"
        closeLabel="Cancel verification"
        description={pendingCode && <>Enter the 6-digit code sent to <b className="text-cocoa">{pendingCode.email}</b>.</>}
      >
        <input
          className="field mt-2 text-center text-2xl font-bold tracking-[0.5em]"
          aria-label="Verification code"
          maxLength={6}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
        />
        {confirmCode.isError && <p role="alert" className="mt-2 text-sm font-bold text-ribbon-ink">{confirmCode.error.message}</p>}
        <button className="btn-primary mt-5 w-full" disabled={code.length !== 6 || confirmCode.isPending} onClick={() => confirmCode.mutate()}>{confirmCode.isPending ? "Verifying…" : "Verify and continue"}</button>
        <button className="mt-2 min-h-11 w-full text-sm font-bold text-muted hover:text-cocoa disabled:text-cocoa/30" disabled={cooldown > 0 || sendCode.isPending} onClick={() => pendingCode && sendCode.mutate(pendingCode.email)}>{cooldown > 0 ? `Resend code in ${cooldown}s` : sendCode.isPending ? "Resending…" : "Resend code"}</button>
      </Dialog>
    </section>
  );
}

function Field({ label, error, optional, children }: { label: string; error?: string; optional?: boolean; children: React.ReactNode }) {
  return <label className="block"><span className="label">{label}{optional && <span className="font-normal text-muted"> optional</span>}</span>{children}{error && <span role="alert" className="mt-1.5 block text-sm font-bold text-ribbon-ink">{error}</span>}</label>;
}

const CHECKOUT_REQUEST_KEY = "minis-checkout-request";

function checkoutIdempotencyKey(requestBody: string) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(CHECKOUT_REQUEST_KEY) ?? "null") as {
      requestBody?: string;
      idempotencyKey?: string;
    } | null;
    if (saved?.requestBody === requestBody && saved.idempotencyKey) {
      return saved.idempotencyKey;
    }
  } catch {
    // A malformed or unavailable saved attempt should not block checkout.
  }

  const idempotencyKey = crypto.randomUUID();
  try {
    sessionStorage.setItem(CHECKOUT_REQUEST_KEY, JSON.stringify({ requestBody, idempotencyKey }));
  } catch {
    // The request can still proceed when browser storage is unavailable.
  }
  return idempotencyKey;
}
